// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import debug from 'debug';
import React from 'react';
import { Constants } from '../../Constants';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat';
import { AuthorRoles, IChatMessage } from '../../libs/models/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addMessageToConversationFromUser, setIsLoadingMessages } from '../../redux/features/conversations/conversationsSlice';
import { SharedStyles } from '../../styles';
import { ChatInput } from './ChatInput';
import { ChatHistory } from './chat-history/ChatHistory';

const log = debug(Constants.debug.root).extend('chat-room');

const useClasses = makeStyles({
    root: {
        ...shorthands.overflow('hidden'),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
    },
    scroll: {
        ...shorthands.margin(tokens.spacingVerticalXS),
        ...SharedStyles.scroll,
    },
    history: {
        ...shorthands.padding(tokens.spacingVerticalM),
        marginLeft: '40px',
        paddingRight: '40px',
        display: 'flex',
        justifyContent: 'center',
    },
    input: {
        ...shorthands.padding(tokens.spacingVerticalM),
    }
});


interface ChatRoomProps {
    hiddenChat: boolean;
}

export const ChatRoom: React.FC<ChatRoomProps> = (props) => {
    const { conversations, selectedId, isLoadingMessages } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);

    const messages = conversations[selectedId].messages;

    const classes = useClasses();
    const dispatch = useAppDispatch();
    const scrollViewTargetRef = React.useRef<HTMLDivElement>(null);
    const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);

    const [isDraggingOver, setIsDraggingOver] = React.useState(false);
    const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };
    const onDragLeave = (e: React.DragEvent<HTMLDivElement | HTMLTextAreaElement>) => {
        e.preventDefault();
        setIsDraggingOver(false);
    };

    const chat = useChat();

    React.useEffect(() => {
        if (!shouldAutoScroll) return;
        scrollViewTargetRef.current?.scrollTo(0, scrollViewTargetRef.current.scrollHeight);
    }, [messages, shouldAutoScroll]);

    
    React.useEffect(() => {
        const getMessages = async () => {
            dispatch(setIsLoadingMessages(true));
            const chatMessages: IChatMessage[] = await chat.getChatMessages(selectedId);
            messages.push(...chatMessages);
            dispatch(setIsLoadingMessages(false));
        };

        if (conversations[selectedId].messages.length === 0) {
            getMessages()
                .catch((e) => {
                    log('error getting messages', e);
                    dispatch(setIsLoadingMessages(false));
                });
        }
        
        setShouldAutoScroll(true);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    React.useEffect(() => {

        const onScroll = () => {
            if (!scrollViewTargetRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = scrollViewTargetRef.current;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
            setShouldAutoScroll(isAtBottom);
        };

        if (!scrollViewTargetRef.current) return;

        const currentScrollViewTarget = scrollViewTargetRef.current;

        currentScrollViewTarget.addEventListener('scroll', onScroll);
        return () => {
            currentScrollViewTarget.removeEventListener('scroll', onScroll);
        };

    }, []);

    const handleSubmit = async (options: GetResponseOptions) => {
        log('submitting user chat message');

        const chatInput: IChatMessage = {
            chatId: selectedId,
            timestamp: new Date().getTime(),
            userId: activeUserInfo?.id as string,
            userName: activeUserInfo?.username as string,
            content: options.value,
            type: options.messageType,
            authorRole: AuthorRoles.User,
        };

        dispatch(addMessageToConversationFromUser({ message: chatInput, chatId: selectedId }));

        await chat.getResponse(options);

        setShouldAutoScroll(true);
    };

    
    {
        return props.hiddenChat ? (
            <div className={classes.root}>
                <div ref={scrollViewTargetRef} className={classes.scroll}>
                    <div className={classes.history}>
                        <ChatHistory messages={messages} onGetResponse={handleSubmit} loadingMessages={isLoadingMessages} />
                    </div>
                </div>
            </div>
        ) : (
            <div className={classes.root} onDragEnter={onDragEnter} onDragOver={onDragEnter} onDragLeave={onDragLeave}>
                <div ref={scrollViewTargetRef} className={classes.scroll}>
                    <div className={classes.history}>
                        <ChatHistory messages={messages} onGetResponse={handleSubmit} loadingMessages={isLoadingMessages} />
                    </div>
                </div>
                <div className={classes.input }>
                    <ChatInput isDraggingOver={isDraggingOver} onDragLeave={onDragLeave} onSubmit={handleSubmit} />
                </div>
            </div>
        )
    }
}